import { queryOptions } from "@tanstack/react-query";
import { billingPublicClient } from "@/shared/lib/supabase";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import type { TenantCreditUsagePublicRow } from "@/types/supabase.types";
import { CONTACT_SALES_URL, CREDIT_DETAILS_PATH } from "./constants";
import type { TenantCreditUsage } from "./types";

type BillingCreditUsageClient = {
  from: (table: "tenant_credit_usage_public") => {
    select: (columns: "*") => {
      maybeSingle: () => PromiseLike<{
        data: TenantCreditUsagePublicRow | null;
        error: Error | null;
      }>;
    };
  };
};

function mapTenantCreditUsage(
  row: TenantCreditUsagePublicRow,
): TenantCreditUsage {
  const creditsLimit = row.credits_limit ?? 0;
  const creditsUsed = row.credits_used ?? 0;
  const averageAnalysisCredits = row.average_analysis_credits ?? 1;
  const projectedCreditsUsed = row.projected_credits_used ?? 1;
  const canStartAnalysis = row.can_start_analysis ?? false;
  const creditsRemaining = roundCredits(creditsLimit - creditsUsed);
  const projectedCreditsOverLimit = Math.max(
    roundCredits(projectedCreditsUsed - creditsLimit),
    0,
  );
  const usageRatio = creditsLimit > 0 ? creditsUsed / creditsLimit : 1;
  const usageStatus =
    creditsUsed > creditsLimit
      ? "over_limit"
      : usageRatio >= 0.8
        ? "near_limit"
        : "ok";

  return {
    creditsLimit,
    creditsUsed,
    periodStart: row.period_start,
    periodEnd: row.period_end,
    averageAnalysisCredits,
    projectedCreditsUsed,
    canStartAnalysis,
    creditsRemaining,
    projectedCreditsOverLimit,
    usageStatus,
    nextAnalysisStatus: canStartAnalysis
      ? "can_start"
      : "blocked_by_projection",
    detailsPath: CREDIT_DETAILS_PATH,
    contactUrl: CONTACT_SALES_URL,
  };
}

function roundCredits(value: number): number {
  return Math.round(value * 100) / 100;
}

export async function fetchTenantCreditUsage(): Promise<TenantCreditUsage | null> {
  const billingClient =
    billingPublicClient as unknown as BillingCreditUsageClient;
  const { data, error } = await billingClient
    .from("tenant_credit_usage_public")
    .select("*")
    .maybeSingle();

  if (error) throw error;
  return data ? mapTenantCreditUsage(data) : null;
}

const base = getKeyOnlyQueryConfig(["billing", "credit-usage"]);

export const billingQueries = {
  all: base,
  currentCreditUsage: () =>
    queryOptions({
      queryKey: [...base.queryKey, "current"],
      queryFn: fetchTenantCreditUsage,
    }),
};
